import { prices } from '@/static/billingData';
import * as Sentry from '@sentry/nextjs';
import type { BillingInterval, SubscriptionPlan } from '@unriddle/db/types';

type PlanId = 'basic' | 'pro' | 'premium' | 'team' | 'unlimited';

export const getPriceId = (
  planId: PlanId,
  billingInterval: 'monthly' | 'yearly',
) => {
  const priceIds = {
    basic: {
      monthly: process.env.NEXT_PUBLIC_US_STRIPE_BASIC_MONTHLY as string,
      yearly: process.env.NEXT_PUBLIC_US_STRIPE_BASIC_YEARLY as string,
    },
    pro: {
      monthly: process.env.NEXT_PUBLIC_US_STRIPE_PRO_MONTHLY as string,
      yearly: process.env.NEXT_PUBLIC_US_STRIPE_PRO_YEARLY as string,
    },
    premium: {
      monthly: process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_MONTHLY as string,
      yearly: process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_YEARLY as string,
    },
    team: {
      monthly: process.env.NEXT_PUBLIC_US_STRIPE_TEAM_MONTHLY as string,
      yearly: process.env.NEXT_PUBLIC_US_STRIPE_TEAM_YEARLY as string,
    },
    unlimited: {
      monthly: process.env.NEXT_PUBLIC_US_STRIPE_UNLIMITED_MONTHLY as string,
      yearly: process.env.NEXT_PUBLIC_US_STRIPE_UNLIMITED_YEARLY as string,
    },
  };

  if (!priceIds[planId]) {
    console.error(`Invalid planId: ${planId}`);
    Sentry.captureException(new Error(`Invalid planId: ${planId}`));
    return null;
  }

  return priceIds[planId][billingInterval];
};

export const getPlanInfo = (
  priceId: string,
): { plan: SubscriptionPlan; billingInterval: BillingInterval } => {
  switch (priceId) {
    case process.env.NEXT_PUBLIC_US_STRIPE_BASIC_MONTHLY:
      return { plan: 'BASIC', billingInterval: 'MONTHLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_BASIC_YEARLY:
      return { plan: 'BASIC', billingInterval: 'YEARLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_PRO_MONTHLY:
    case process.env.NEXT_PUBLIC_US_STRIPE_PRO_MONTHLY_V1:
      return { plan: 'PRO', billingInterval: 'MONTHLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_PRO_YEARLY:
    case process.env.NEXT_PUBLIC_US_STRIPE_PRO_YEARLY_V1:
    case process.env.NEXT_PUBLIC_US_STRIPE_PRO_YEARLY_V2:
      return { plan: 'PRO', billingInterval: 'YEARLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_MONTHLY:
    case process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_MONTHLY_V1:
    case process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_MONTHLY_LEGACY:
      return { plan: 'PREMIUM', billingInterval: 'MONTHLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_YEARLY:
    case process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_YEARLY_V1:
    case process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_YEARLY_V2:
    case process.env.NEXT_PUBLIC_US_STRIPE_PREMIUM_YEARLY_LEGACY:
      return { plan: 'PREMIUM', billingInterval: 'YEARLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_UNLIMITED_MONTHLY:
      return { plan: 'UNLIMITED', billingInterval: 'MONTHLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_UNLIMITED_YEARLY:
      return { plan: 'UNLIMITED', billingInterval: 'YEARLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_TEAM_MONTHLY:
    case process.env.NEXT_PUBLIC_US_STRIPE_TEAM_MONTHLY_V1:
    case 'price_1Nu06ZEFIFRfcUgVVtoBc9Qm':
      return { plan: 'TEAM', billingInterval: 'MONTHLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_TEAM_YEARLY:
    case process.env.NEXT_PUBLIC_US_STRIPE_TEAM_YEARLY_V1:
    case process.env.NEXT_PUBLIC_US_STRIPE_TEAM_YEARLY_V2:
    case 'price_1PCORjEFIFRfcUgVSX4RXrJz':
      return { plan: 'TEAM', billingInterval: 'YEARLY' };
    case process.env.NEXT_PUBLIC_US_STRIPE_FRIEND:
      return { plan: 'FRIEND', billingInterval: 'MONTHLY' };
    default:
      throw new Error('Invalid price ID');
  }
};

const capitalizeFirstLetter = (string: string) => {
  return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
};

export const getPrice = (planInfo: {
  plan: SubscriptionPlan;
  billingInterval: BillingInterval;
}) => {
  return (
    prices.USD[capitalizeFirstLetter(planInfo.plan) as 'Pro' | 'Premium'][
      capitalizeFirstLetter(planInfo.billingInterval) as 'Monthly' | 'Yearly'
    ].Price || 0
  );
};
